import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IS_PREVIEW } from "../lib/pro-mode";
import { useContactCount, useProfile } from "./useProData";
import { useMarketplaceGate, type MarketplaceGate } from "./useMarketplaceGate";

/**
 * First-run routing: a freshly admitted pro who lands on `/` or `/home`
 * with zero contacts and no prior visit gets bounced once to `/welcome` —
 * the real "get set up" checklist — instead of an empty book.
 *
 *   - One-shot forever: the localStorage flag is set on the FIRST
 *     evaluation regardless of whether a redirect actually fired, so a
 *     returning pro (including one who was never redirected because their
 *     book already had contacts) is never redirected again.
 *   - Deep links: any path other than `/` or `/home` is never touched —
 *     a pro who lands directly on e.g. `/clients/:id` from a notification
 *     link stays there.
 *   - Skipped entirely under IS_PREVIEW (no backend contact count to
 *     evaluate against, and preview fixtures should render deterministically
 *     at whatever route the fixture URL asked for).
 *
 * Plan 476: with `marketplace_go_live_enabled` ON, a new AGENT stays on Home,
 * whose setup checklist (or out-of-area notice) is the first-run surface; no
 * `/welcome` bounce. Gate OFF, or a lender, goes to `/welcome` as before. While
 * the gate or the role is unresolved the decision waits, so it never bounces
 * early off a not-yet-loaded flag.
 *
 * Mount this once at the authenticated shell (see App.tsx `AuthedLayout`) —
 * NOT per-page — so it evaluates exactly once per app session regardless of
 * which authed route happens to render first.
 */
export const WELCOME_SEEN_KEY = "sill_welcome_seen";

function readSeenFlag(): boolean {
  try {
    return window.localStorage.getItem(WELCOME_SEEN_KEY) !== null;
  } catch {
    // Fail open: if we can't confirm "not yet seen", don't risk redirecting
    // a returning pro on every load — treat storage-unavailable as seen.
    return true;
  }
}

function writeSeenFlag(): void {
  try {
    window.localStorage.setItem(WELCOME_SEEN_KEY, "1");
  } catch {
    // Best-effort only — worst case this evaluates again next session.
  }
}

/**
 * Pure decision function — unit-testable without React, router, or
 * localStorage. `path` is the app's INITIAL path for this session (captured
 * once, not re-evaluated after later client-side navigation); `seen` is
 * whether the one-shot flag was already set; `count` is the pro's current
 * contact count.
 */
export function shouldRedirectToWelcome(
  path: string,
  seen: boolean,
  count: number,
): boolean {
  if (seen) return false;
  if (path !== "/" && path !== "/home") return false;
  return count === 0;
}

/** What first run does once the contact count has settled. */
export type FirstRunDestination = "welcome" | "home" | "wait" | "none";

/**
 * Pure routing matrix. `role` is null until the profile resolves. An
 * out-of-area agent is an agent: they stay on their Home (the CRM-only,
 * out-of-area variant), so the area verdict never enters the decision.
 */
export function firstRunDestination(
  path: string,
  seen: boolean,
  count: number,
  gate: MarketplaceGate,
  role: "agent" | "lo" | null,
): FirstRunDestination {
  if (!shouldRedirectToWelcome(path, seen, count)) return "none";
  // A lender goes to /welcome whatever the gate says, so never wait on flags.
  if (role === "lo") return "welcome";
  if (gate === "loading") return "wait";
  if (gate === "off") return "welcome";
  if (role === null) return "wait";
  return role === "agent" ? "home" : "welcome";
}

export function useFirstRunWelcome(): void {
  const navigate = useNavigate();
  const location = useLocation();
  // Capture the one-shot flag as of mount: once it is set (or under
  // IS_PREVIEW) the decision below can never redirect, so the count fetch
  // is skipped entirely — a returning pro pays zero network for this probe.
  //
  // useState's lazy initializer, not useRef: this value IS needed during
  // render (it gates the fetch), and a ref's `.current` must not be read
  // there (react-hooks/refs). The initializer still runs exactly once, so
  // a later flag write cannot re-enable the fetch mid-session.
  const [seenAtMount] = useState(() => IS_PREVIEW || readSeenFlag());
  const contactCount = useContactCount({ enabled: !seenAtMount });
  const gate = useMarketplaceGate();
  const profile = useProfile();
  const role = profile.data?.role ?? null;

  // Capture the path as of first render only — later client-side navigation
  // (e.g. the pro clicking around while the count request is still in
  // flight) must not change what "the initial path" was for this decision.
  const initialPathRef = useRef(location.pathname);
  const evaluatedRef = useRef(false);

  useEffect(() => {
    if (IS_PREVIEW) return;
    if (evaluatedRef.current) return;
    // Wait for the count request to settle (success OR failure) before
    // deciding — an in-flight load must not race a redirect off a
    // not-yet-loaded "0 contacts".
    if (contactCount.isLoading) return;

    const seen = readSeenFlag();
    // A failed count load resolves to `null`, not `0` — never redirect off
    // an unknown count. The flag is still set (one-shot forever regardless
    // of outcome).
    const count = contactCount.data;
    const destination =
      count === null
        ? "none"
        : firstRunDestination(initialPathRef.current, seen, count, gate, role);
    // Gate or role still resolving: decide on a later pass, never early.
    if (destination === "wait") return;

    evaluatedRef.current = true;
    if (destination === "welcome") navigate("/welcome", { replace: true });
    writeSeenFlag();
  }, [contactCount.isLoading, contactCount.data, gate, role, navigate]);
}
